import React, { useState, useEffect } from 'react';
import './Pages.css';

const Products = ({ setActiveMenu, userRole }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [insuredItems, setInsuredItems] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [newProduct, setNewProduct] = useState({
    productCode: '',
    productName: '',
    productDescription: '',
    productColor: '#667eea',
    productIcon: 'box'
  });
  
  const [newInsuredItems, setNewInsuredItems] = useState([
    { insuredItemName: '', rate: '', calculationType: 1 }
  ]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/products');
      const data = await response.json();

      if (data.success) {
        setProducts(data.products);
      } else {
        setError('Failed to load products');
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (milliseconds) => {
    if (!milliseconds) return 'N/A';
    const date = new Date(parseInt(milliseconds));
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleViewProduct = async (productId) => {
    setShowModal(true);
    
    try {
      const response = await fetch(`https://main.victriedemos.com:3017/api/products/${productId}`);
      const data = await response.json();

      if (data.success) {
        setSelectedProduct(data.product);
        setInsuredItems(data.insuredItems);
      } else {
        alert('Failed to load product details');
      }
    } catch (err) {
      console.error('Error fetching product details:', err);
      alert('Unable to load product details');
    }
  };

  const handleDisableProduct = async (productId, productName) => {
    if (!window.confirm(`Are you sure you want to disable "${productName}"?`)) {
      return;
    }

    try {
      const response = await fetch(`https://main.victriedemos.com:3017/api/products/${productId}/disable`, {
        method: 'PUT'
      });
      const data = await response.json();

      if (data.success) {
        alert('Product disabled successfully');
        fetchProducts(); // Refresh the list
      } else {
        alert('Failed to disable product');
      }
    } catch (err) {
      console.error('Error disabling product:', err);
      alert('Unable to disable product');
    }
  };

  const handleEnableProduct = async (productId, productName) => {
    if (!window.confirm(`Are you sure you want to enable "${productName}"?`)) {
      return;
    }

    try {
      const response = await fetch(`https://main.victriedemos.com:3017/api/products/${productId}/enable`, {
        method: 'PUT'
      });
      const data = await response.json();

      if (data.success) {
        alert('Product enabled successfully');
        fetchProducts(); // Refresh the list
      } else {
        alert('Failed to enable product');
      }
    } catch (err) {
      console.error('Error enabling product:', err);
      alert('Unable to enable product');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
    setInsuredItems([]);
  };

  const handleAddInsuredItem = () => {
    setNewInsuredItems([...newInsuredItems, { insuredItemName: '', rate: '', calculationType: 1 }]);
  };

  const handleRemoveInsuredItem = (index) => {
    const items = newInsuredItems.filter((_, i) => i !== index);
    setNewInsuredItems(items);
  };

  const handleInsuredItemChange = (index, field, value) => {
    const items = [...newInsuredItems];
    items[index][field] = value;
    setNewInsuredItems(items);
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    
    if (!newProduct.productCode || !newProduct.productName) {
      alert('Product code and name are required');
      return;
    }
    
    // Filter out empty insured items
    const validInsuredItems = newInsuredItems.filter(item => 
      item.insuredItemName.trim() !== '' && item.rate.trim() !== ''
    );
    
    setSubmitting(true);
    
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          productCode: newProduct.productCode,
          productName: newProduct.productName,
          productDescription: newProduct.productDescription,
          productColor: newProduct.productColor,
          productIcon: newProduct.productIcon,
          createdBy: localStorage.getItem('userEmail'),
          insuredItems: validInsuredItems
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('Product created successfully');
        setShowCreateModal(false);
        // Reset form
        setNewProduct({
          productCode: '',
          productName: '',
          productDescription: '',
          productColor: '#667eea',
          productIcon: 'box'
        });
        setNewInsuredItems([{ insuredItemName: '', rate: '', calculationType: 1 }]);
        fetchProducts(); // Refresh product list
      } else {
        alert(data.message || 'Failed to create product');
      }
    } catch (err) {
      console.error('Error creating product:', err);
      alert('Unable to create product');
    } finally {
      setSubmitting(false);
    }
  };

  const getCalculationTypeText = (type) => {
    if (type === 1) return 'Percentage';
    if (type === 2) return 'Amount';
    if (type === 3) return 'Rate Table';
    return 'N/A';
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Products</h1>
        </div>
        <div className="loading">Loading products...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Products</h1>
          <p className="page-description">Manage insurance products and their insured items</p>
        </div>
        <button className="primary-btn" onClick={() => setShowCreateModal(true)}>
          <i className="bi bi-plus-circle"></i> Add Product
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="products-grid">
        {products.length === 0 ? (
          <div className="no-data">No products found</div>
        ) : (
          products.map((product) => (
            <div 
              key={product.Id} 
              className={`product-card ${product.Status === 2 ? 'disabled' : ''}`}
              style={{ backgroundColor: product.Status === 2 ? '#e0e0e0' : (product.ProductColor || '#f5f5f5') }}
            >
              <div className="product-card-content">
                {product.Status === 2 && (
                  <div className="disabled-badge">
                    <i className="bi bi-x-circle"></i> Disabled
                  </div>
                )}
                <div className="product-icon">
                  <i className={`bi bi-${product.ProductIcon || 'box'}`}></i>
                </div>
                <h3 className="product-name">{product.ProductName || 'N/A'}</h3>
                <p className="product-description">
                  {product.ProductDescription 
                    ? (product.ProductDescription.length > 100 
                        ? product.ProductDescription.substring(0, 100) + '...' 
                        : product.ProductDescription)
                    : 'No description available'
                  }
                </p>
                <div className="product-policy-count">
                  <i className="bi bi-file-text"></i>
                  <span>{product.PolicyCount || 0} {product.PolicyCount === 1 ? 'policy' : 'policies'} booked</span>
                </div>
                <div className="product-actions">
                  <button 
                    className="btn-view-product"
                    onClick={() => handleViewProduct(product.Id)}
                    title="View Product"
                  >
                    <i className="bi bi-eye"></i> View
                  </button>
                  {product.Status === 1 ? (
                    <button 
                      className="btn-disable-product"
                      onClick={() => handleDisableProduct(product.Id, product.ProductName)}
                      title="Disable Product"
                    >
                      <i className="bi bi-x-circle"></i> Disable
                    </button>
                  ) : (
                    <button 
                      className="btn-enable-product"
                      onClick={() => handleEnableProduct(product.Id, product.ProductName)}
                      title="Enable Product"
                    >
                      <i className="bi bi-check-circle"></i> Enable
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Product Details Modal */}
      {showModal && selectedProduct && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Product Details</h2>
              <button className="close-button" onClick={closeModal}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="modal-body">
              {/* Product Information */}
              <div className="detail-section">
                <h3>Product Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Product Code:</label>
                    <span>{selectedProduct.ProductCode || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Product Name:</label>
                    <span>{selectedProduct.ProductName || 'N/A'}</span>
                  </div>
                  <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                    <label>Description:</label>
                    <span>{selectedProduct.ProductDescription || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Color:</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div 
                        style={{ 
                          width: '30px', 
                          height: '30px', 
                          backgroundColor: selectedProduct.ProductColor || '#000',
                          border: '1px solid #ccc',
                          borderRadius: '4px'
                        }}
                      ></div>
                      <span>{selectedProduct.ProductColor || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <label>Icon:</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <i className={`bi bi-${selectedProduct.ProductIcon || 'box'}`} style={{ fontSize: '24px' }}></i>
                      <span>{selectedProduct.ProductIcon || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <label>Created By:</label>
                    <span>{selectedProduct.CreatedByName || selectedProduct.CreatedBy || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Date Created:</label>
                    <span>{formatDate(selectedProduct.DateCreated)}</span>
                  </div>
                </div>
              </div>

              {/* Insured Items Section */}
              <div className="detail-section" style={{ marginTop: '20px' }}>
                <h3>Insured Items</h3>
                {insuredItems.length === 0 ? (
                  <p className="no-data">No insured items found for this product</p>
                ) : (
                  <div className="table-container" style={{ maxHeight: '300px', overflow: 'auto' }}>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Item Name</th>
                          <th>Rate</th>
                          <th>Calculation Type</th>
                          <th>Created By</th>
                          <th>Date Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {insuredItems.map((item, index) => (
                          <tr key={item.Id}>
                            <td>{index + 1}</td>
                            <td>{item.InsuredItemName || 'N/A'}</td>
                            <td>{item.Rate || 'N/A'}</td>
                            <td>{getCalculationTypeText(item.CalculationType)}</td>
                            <td>{item.CreatedByName || item.CreatedBy || 'N/A'}</td>
                            <td>{formatDate(item.DateCreated)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={closeModal} className="btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Create Product Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content large-modal create-product-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Product</h2>
              <button className="close-button" onClick={() => setShowCreateModal(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <form onSubmit={handleCreateProduct}>
              <div className="modal-body">
                {/* Product Information */}
                <div className="detail-section">
                  <h3>Product Information</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Product Code <span className="required">*</span></label>
                      <input
                        type="text"
                        value={newProduct.productCode}
                        onChange={(e) => setNewProduct({...newProduct, productCode: e.target.value})}
                        placeholder="e.g., PSV-001"
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Product Name <span className="required">*</span></label>
                      <input
                        type="text"
                        value={newProduct.productName}
                        onChange={(e) => setNewProduct({...newProduct, productName: e.target.value})}
                        placeholder="e.g., PSV Insurance"
                        required
                      />
                    </div>
                    
                    <div className="form-group full-width">
                      <label>Description</label>
                      <textarea
                        value={newProduct.productDescription}
                        onChange={(e) => setNewProduct({...newProduct, productDescription: e.target.value})}
                        placeholder="Enter product description"
                        rows="3"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Product Color</label>
                      <div className="color-picker-container">
                        <input
                          type="color"
                          value={newProduct.productColor}
                          onChange={(e) => setNewProduct({...newProduct, productColor: e.target.value})}
                          className="color-picker"
                        />
                        <input
                          type="text"
                          value={newProduct.productColor}
                          onChange={(e) => setNewProduct({...newProduct, productColor: e.target.value})}
                          placeholder="#667eea"
                          className="color-hex-input"
                        />
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label>Icon Name</label>
                      <div className="icon-input-container">
                        <input
                          type="text"
                          value={newProduct.productIcon}
                          onChange={(e) => setNewProduct({...newProduct, productIcon: e.target.value})}
                          placeholder="e.g., bus, car, shield"
                        />
                        <i className={`bi bi-${newProduct.productIcon || 'box'}`} style={{ fontSize: '24px' }}></i>
                      </div>
                      <small className="form-hint">Browse icons at: https://icons.getbootstrap.com/</small>
                    </div>
                  </div>
                </div>

                {/* Insured Items Section */}
                <div className="detail-section" style={{ marginTop: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3>Insured Items</h3>
                    <button 
                      type="button" 
                      className="btn-add-item" 
                      onClick={handleAddInsuredItem}
                    >
                      <i className="bi bi-plus-circle"></i> Add Item
                    </button>
                  </div>
                  
                  {newInsuredItems.map((item, index) => (
                    <div key={index} className="insured-item-row">
                      <div className="form-group">
                        <label>Item Name</label>
                        <input
                          type="text"
                          value={item.insuredItemName}
                          onChange={(e) => handleInsuredItemChange(index, 'insuredItemName', e.target.value)}
                          placeholder="e.g., Comp, TPO"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Rate</label>
                        <input
                          type="text"
                          value={item.rate}
                          onChange={(e) => handleInsuredItemChange(index, 'rate', e.target.value)}
                          placeholder="e.g., 2.5 or 5000"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Calculation Type</label>
                        <select
                          value={item.calculationType}
                          onChange={(e) => handleInsuredItemChange(index, 'calculationType', parseInt(e.target.value))}
                        >
                          <option value={1}>Percentage</option>
                          <option value={2}>Amount</option>
                          <option value={3}>Rate Table</option>
                        </select>
                      </div>
                      
                      {newInsuredItems.length > 1 && (
                        <button 
                          type="button" 
                          className="btn-remove-item" 
                          onClick={() => handleRemoveInsuredItem(index)}
                          title="Remove Item"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  onClick={() => setShowCreateModal(false)} 
                  className="btn-secondary"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="primary-btn"
                  disabled={submitting}
                >
                  {submitting ? 'Creating...' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
